import { useState } from 'react'
import { useTicket } from '@/queries/tickets'
import { MessageThread } from './MessageThread'
import { ContextPanel } from './ContextPanel'
import { ReplyComposer } from './ReplyComposer'
import { FullPageSpinner } from '@/components/etainabl-spinner'
import { ViewingAsBanner } from '@/features/view-as/ViewingAsBanner'
import { InvestigationPanel } from '@/features/ai-investigate/InvestigationPanel'
import type { ReactNode } from 'react'

export function TicketDetailView({ ticketId, adminControls, asUser, showInvestigations }: {
  ticketId: string
  adminControls?: ReactNode
  asUser?: string
  showInvestigations?: boolean
}) {
  const { data, isLoading, error } = useTicket(ticketId, asUser)
  // Remounting the composer (via `key`) is how a fresh seed overwrites
  // whatever the user was already typing, instead of colliding with it.
  const [draftSeed, setDraftSeed] = useState<{ text: string; v: number } | null>(null)
  if (isLoading) return <FullPageSpinner />

  // A forged asUser on a non-admin session gets an explicit message, not a
  // blank "not found" that reads as a broken link.
  const status = (error as { status?: number; statusCode?: number } | null)?.status
    ?? (error as { statusCode?: number } | null)?.statusCode
  if (status === 403) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        You don&apos;t have access to view as another user.
      </div>
    )
  }

  if (error || !data) return <div className="p-8">Ticket not found.</div>

  return (
    // -mx-8 -my-6 cancels the AppShell main's padding so the detail pane is
    // full-bleed. h-screen + cancelled padding makes the pane fit the viewport
    // exactly, so the composer never sits below the fold.
    <div className="flex h-screen -mx-8 -my-6 flex-col">
      {data.viewingAs && <ViewingAsBanner name={data.viewingAs.name} companyName={data.viewingAs.companyName} />}
      <div className="flex flex-1 min-h-0">
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <header className="p-4 border-b flex items-center gap-4 shrink-0">
            <h1 className="text-xl font-semibold flex-1 min-w-0 truncate">{data.ticket.subject}</h1>
          </header>
          <div className="flex-1 overflow-y-auto min-h-0">
            <MessageThread ticketId={ticketId} messages={data.messages} />
            {showInvestigations && (
              <InvestigationPanel
                ticketId={ticketId}
                onUseAsReply={text => setDraftSeed(s => ({ text, v: (s?.v ?? 0) + 1 }))}
              />
            )}
          </div>
          <div className="shrink-0 border-t bg-background">
            {data.viewingAs ? (
              <div className="p-4 border-t text-sm text-muted-foreground">
                This is a read-only view — replies are disabled while viewing as another user.
              </div>
            ) : (
              <ReplyComposer
                key={draftSeed?.v ?? 0}
                ticketId={ticketId}
                status={data.ticket.status}
                viewer={data.viewerRelation}
                initialBody={draftSeed?.text}
              />
            )}
          </div>
        </div>
        <aside className="w-72 shrink-0 border-l border-border flex flex-col bg-background overflow-y-auto">
          <div className="divide-y divide-border">
            {adminControls}
            <ContextPanel ticket={data.ticket} links={data.links} viewingAs={data.viewingAs} />
          </div>
        </aside>
      </div>
    </div>
  )
}
